import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import Swal from 'sweetalert2';
import { DeviceDetectorService } from '../../../../services/device-detector.service';
import { DeliveryService } from '../../../../services/delivery';

@Component({
  selector: 'app-adddeliveryhero',
  templateUrl: './adddeliveryhero.component.html',
  styleUrls: ['./adddeliveryhero.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AdddeliveryheroComponent implements OnInit {
  contentReady = false;
  isMobile = false;
  isAr = false;
  submitted = false;
  formInfo!: FormGroup;

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private deliveryService: DeliveryService,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobileDevice();
    this.isAr = localStorage.getItem('language') === 'ar';

    this.formInfo = this.fb.group(
      {
        name_ar: [
          '',
          [Validators.required, Validators.minLength(2), Validators.maxLength(255)]
        ],
        name_en: [
          '',
          [Validators.required, Validators.minLength(2), Validators.maxLength(255)]
        ],
        phone: [
          '',
          [Validators.required, Validators.pattern(/^\d{11}$/)]
        ],
        number: [''],
        national_id: [''],

        delivery_vehicle_id: [null],
        commission_type: ['fixed', [Validators.required]],
        commission_value: [0, [Validators.required, Validators.min(0)]],

        make_it_employee: [false],
        job_title: [''],
        salary: [null]
      },
      { validators: this.employeeFieldsValidator() }
    );

    this.handleEmployeeMode();
  }

  private handleEmployeeMode(): void {
    this.formInfo.get('make_it_employee')?.valueChanges.subscribe((checked: boolean) => {
      const jobTitle = this.formInfo.get('job_title');
      const salary = this.formInfo.get('salary');

      if (checked) {
        jobTitle?.setValidators([Validators.required, Validators.maxLength(255)]);
        salary?.setValidators([Validators.required, Validators.min(0)]);
      } else {
        jobTitle?.clearValidators();
        salary?.clearValidators();
        jobTitle?.setValue('');
        salary?.setValue(null);
      }

      jobTitle?.updateValueAndValidity();
      salary?.updateValueAndValidity();
    });
  }

  private employeeFieldsValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const makeItEmployee = group.get('make_it_employee')?.value;
      const jobTitle = group.get('job_title')?.value;
      const salary = group.get('salary')?.value;

      if (makeItEmployee) {
        if (!jobTitle || salary === null || salary === undefined || salary === '') {
          return { employeeFieldsRequired: true };
        }
      }

      return null;
    };
  }

  shouldShowError(controlName: string): boolean {
    const control = this.formInfo?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted);
  }

  getErrorMessage(controlName: string, label: string): string {
    const control = this.formInfo?.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.isAr ? `${label} مطلوب` : `${label} is required`;
    }

    if (control.errors['minlength']) {
      const requiredLength = control.errors['minlength'].requiredLength;
      return this.isAr
        ? `${label} لازم يكون ${requiredLength} حروف على الأقل`
        : `${label} must be at least ${requiredLength} characters`;
    }

    if (control.errors['maxlength']) {
      const requiredLength = control.errors['maxlength'].requiredLength;
      return this.isAr
        ? `${label} لا يزيد عن ${requiredLength} حرف`
        : `${label} must not exceed ${requiredLength} characters`;
    }

    if (control.errors['pattern']) {
      return this.isAr
        ? `${label} لازم يكون 11 رقم انجليزي`
        : `${label} must be 11 English digits`;
    }

    if (control.errors['min']) {
      return this.isAr
        ? `${label} لازم يكون صفر أو أكبر`
        : `${label} must be zero or more`;
    }

    return this.isAr ? `${label} غير صحيح` : `${label} is invalid`;
  }

  getMissingRequiredCount(): number {
    if (!this.formInfo) {
      return 0;
    }

    return Object.keys(this.formInfo.controls).filter((controlName) => {
      const control = this.formInfo.get(controlName);
      return control?.hasError('required') || control?.invalid;
    }).length;
  }

  private validateBeforeSubmit(): boolean {
    this.submitted = true;

    if (this.formInfo.invalid) {
      this.formInfo.markAllAsTouched();

      Swal.fire({
        icon: 'warning',
        title: this.isAr ? 'تأكد من بيانات الدليفري' : 'Please check delivery hero data',
        text: this.isAr ? 'راجع الرسائل تحت الحقول المطلوبة أو غير الصحيحة' : 'Review the messages below the required or invalid fields',
        confirmButtonText: this.isAr ? 'تمام' : 'OK'
      });

      return false;
    }

    return true;
  }

  onSubmit(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    this.contentReady = true;

    const fv = this.formInfo.value;

    const payload = {
      name_ar: fv.name_ar?.toString().trim(),
      name_en: fv.name_en?.toString().trim(),
      phone: fv.phone?.toString().trim(),
      number: fv.number ? fv.number.toString().trim() : null,
      national_id: fv.national_id ? fv.national_id.toString().trim() : null,

      delivery_vehicle_id: fv.delivery_vehicle_id ? Number(fv.delivery_vehicle_id) : null,
      commission_type: fv.commission_type,
      commission_value: Number(fv.commission_value),
      warehouse_id: Number(localStorage.getItem('posId')),

      make_it_employee: !!fv.make_it_employee,
      job_title: fv.make_it_employee ? fv.job_title?.toString().trim() : null,
      salary: fv.make_it_employee ? Number(fv.salary) : null
    };

    this.deliveryService.createDeliveryStar(payload).subscribe({
      next: () => {
        this.contentReady = false;

        Swal.fire({
          icon: 'success',
          title: this.isAr
            ? 'تم إنشاء Delivery Star بنجاح'
            : 'Delivery Star created successfully',
          text: this.isAr
            ? 'تم إنشاء الحساب وربطه بنجاح'
            : 'User account was created and linked successfully',
          showConfirmButton: false,
          timer: 1800
        });

        const redirectUrl = this.isMobile ? '/deliverystar.mobile' : '/deliverystar';
        this.router.navigateByUrl(redirectUrl);
      },
      error: (err) => {
        this.contentReady = false;

        Swal.fire({
          icon: 'error',
          title: this.isAr ? 'فشل إنشاء Delivery Star' : 'Failed to create Delivery Star',
          text:
            err?.error?.message ||
            (this.isAr ? 'تحقق من البيانات المدخلة' : 'Please check the entered data'),
          confirmButtonText: this.isAr ? 'تمام' : 'OK'
        });
      }
    });
  }
}
